import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import EventSeatIcon from '@mui/icons-material/EventSeat';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useLocation } from 'react-router-dom';
import config from '../../config';
import { requireUserId } from './userSession';

const STATUS_COLORS = {
  Reserved: { bg: '#eff6ff', fg: '#1d4ed8' },
  CheckedIn: { bg: '#ecfdf5', fg: '#047857' },
  Completed: { bg: '#f1f5f9', fg: '#475569' },
  Cancelled: { bg: '#fef2f2', fg: '#b91c1c' },
};

// Local datetime string (yyyy-MM-ddThh:mm) for the datetime-local input.
const toLocalInput = (d) => {
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(
    d.getHours(),
  )}:${p(d.getMinutes())}`;
};

export default function Reservations() {
  const company = JSON.parse(
    sessionStorage.getItem('companyDetails') || '{}',
  );
  const token = sessionStorage.getItem('token');
  const location = useLocation();

  const [scope, setScope] = useState('upcoming');
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState({
    todayCount: 0,
    upcomingCount: 0,
    checkedInCount: 0,
  });
  const [tables, setTables] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const emptyForm = {
    tableId: '',
    customerName: '',
    mobile: '',
    guestCount: 2,
    reservationDateTime: toLocalInput(new Date(Date.now() + 60 * 60 * 1000)),
    durationMinutes: 120,
    notes: '',
  };
  const [form, setForm] = useState(emptyForm);

  const authHeaders = { Authorization: `Bearer ${token}` };

  const loadList = useCallback(() => {
    if (!company?.id) return;
    fetch(
      `${config.API_BASE_URL}/reservation/list/${company.id}?scope=${scope}`,
      { headers: authHeaders },
    )
      .then((r) => r.json())
      .then((d) => setRows(Array.isArray(d) ? d : []))
      .catch(() => setRows([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company?.id, scope]);

  const loadStats = useCallback(() => {
    if (!company?.id) return;
    fetch(`${config.API_BASE_URL}/reservation/stats/${company.id}`, {
      headers: authHeaders,
    })
      .then((r) => r.json())
      .then((d) => d && typeof d === 'object' && setStats(d))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company?.id]);

  const loadTables = useCallback(() => {
    if (!company?.id) return;
    // #13: only AVAILABLE tables (no running order, not already reserved).
    fetch(
      `${config.API_BASE_URL}/restaurantorder/available-tables/${company.id}`,
      { headers: authHeaders },
    )
      .then((r) => r.json())
      .then((d) => setTables(Array.isArray(d) ? d : []))
      .catch(() => setTables([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company?.id]);

  useEffect(() => {
    loadList();
  }, [loadList]);
  useEffect(() => {
    loadStats();
    loadTables();
  }, [loadStats, loadTables]);

  // Auto-open the create dialog when arriving from the "Reserve Table" button.
  useEffect(() => {
    if (new URLSearchParams(location.search).get('new') === '1') openCreate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const openCreate = () => {
    setForm(emptyForm);
    loadTables(); // #13: refresh available tables in real time on open
    setOpen(true);
  };

  const submit = async () => {
    if (!form.tableId) return toast.error('Please select a table');
    if (!form.customerName.trim()) return toast.error('Customer name required');
    if (!/^\d{10}$/.test(form.mobile.trim()))
      return toast.error('Enter a valid 10-digit mobile');
    if (!form.reservationDateTime) return toast.error('Pick date & time');

    const uid = requireUserId();
    if (!uid) return;

    const table = tables.find((t) => String(t.id) === String(form.tableId));
    setSaving(true);
    try {
      const res = await fetch(`${config.API_BASE_URL}/reservation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({
          companyId: company.id,
          tableId: Number(form.tableId),
          tableName: table?.tableName || '',
          areaId: table?.areaid || 0,
          customerName: form.customerName.trim(),
          mobile: form.mobile.trim(),
          guestCount: Number(form.guestCount) || 1,
          reservationDateTime: form.reservationDateTime,
          durationMinutes: Number(form.durationMinutes) || 120,
          notes: form.notes,
          createdBy: uid,
          createdByName:
            JSON.parse(sessionStorage.getItem('user') || '{}')?.name || '',
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || body?.success === false) {
        toast.error(body?.error || body?.message || 'Failed to create reservation');
        return;
      }
      toast.success('Reservation created!');
      setOpen(false);
      loadList();
      loadStats();
    } catch (e) {
      console.error('[Reservations] create failed:', e);
      toast.error('Failed to create reservation');
    } finally {
      setSaving(false);
    }
  };

  const changeStatus = async (id, status) => {
    try {
      const res = await fetch(`${config.API_BASE_URL}/reservation/${id}/status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({ status }),
      });
      if (!res.ok) {
        toast.error('Failed to update status');
        return;
      }
      toast.success(`Marked ${status}`);
      loadList();
      loadStats();
    } catch {
      toast.error('Failed to update status');
    }
  };

  const fmtWhen = (s) => {
    const d = new Date(s);
    return d.toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const statCards = [
    { label: "Today's Reservations", value: stats.todayCount, color: '#1d4ed8' },
    { label: 'Upcoming', value: stats.upcomingCount, color: '#047857' },
    { label: 'Checked In', value: stats.checkedInCount, color: '#b45309' },
  ];

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 } }}>
      <ToastContainer position='top-right' autoClose={2500} />

      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 1.5,
          mb: 2,
        }}>
        <Typography variant='h5' sx={{ fontWeight: 800, color: '#0F172A' }}>
          🍽️ Table Reservations
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title='Refresh'>
            <IconButton onClick={loadList} size='small'>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant='contained'
            startIcon={<AddIcon />}
            onClick={openCreate}
            sx={{ textTransform: 'none', fontWeight: 700 }}>
            New Reservation
          </Button>
        </Box>
      </Box>

      {/* Stat cards */}
      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        {statCards.map((s) => (
          <Grid item xs={4} key={s.label}>
            <Paper
              elevation={0}
              sx={{
                p: { xs: 1.4, md: 2 },
                borderRadius: 3,
                border: '1px solid #E2E8F0',
              }}>
              <Typography sx={{ fontSize: '0.72rem', color: '#64748B', fontWeight: 600 }}>
                {s.label}
              </Typography>
              <Typography sx={{ fontSize: '1.6rem', fontWeight: 900, color: s.color }}>
                {s.value}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Scope filter */}
      <Box sx={{ display: 'flex', gap: 0.6, mb: 1.6 }}>
        {[
          ['upcoming', 'Upcoming'],
          ['today', 'Today'],
          ['all', 'All'],
        ].map(([val, label]) => (
          <Chip
            key={val}
            label={label}
            onClick={() => setScope(val)}
            color={scope === val ? 'primary' : 'default'}
            variant={scope === val ? 'filled' : 'outlined'}
            sx={{ fontWeight: 700 }}
          />
        ))}
      </Box>

      {/* List */}
      {rows.length === 0 ? (
        <Paper
          elevation={0}
          sx={{
            p: 4,
            borderRadius: 3,
            border: '1px dashed #CBD5E1',
            textAlign: 'center',
            color: '#94A3B8',
          }}>
          <EventSeatIcon sx={{ fontSize: 40, mb: 1, opacity: 0.5 }} />
          <Typography>No reservations to show.</Typography>
        </Paper>
      ) : (
        <Grid container spacing={1.5}>
          {rows.map((r) => {
            const sc = STATUS_COLORS[r.status] || STATUS_COLORS.Reserved;
            return (
              <Grid item xs={12} sm={6} md={4} key={r.id}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 1.8,
                    borderRadius: 3,
                    border: '1px solid #E2E8F0',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0.6,
                  }}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}>
                    <Typography sx={{ fontWeight: 800, color: '#0F172A' }}>
                      {r.tableName || `Table ${r.tableId}`}
                    </Typography>
                    <Chip
                      label={r.status}
                      size='small'
                      sx={{ bgcolor: sc.bg, color: sc.fg, fontWeight: 700 }}
                    />
                  </Box>
                  <Typography sx={{ fontSize: '0.85rem', color: '#0F172A', fontWeight: 600 }}>
                    {r.customerName} · 📞 {r.mobile}
                  </Typography>
                  <Typography sx={{ fontSize: '0.8rem', color: '#64748B' }}>
                    🕒 {fmtWhen(r.reservationDateTime)} · 👥 {r.guestCount} guest
                    {r.guestCount > 1 ? 's' : ''}
                  </Typography>
                  {r.notes && (
                    <Typography sx={{ fontSize: '0.78rem', color: '#94A3B8' }}>
                      📝 {r.notes}
                    </Typography>
                  )}

                  {/* Status actions */}
                  {(r.status === 'Reserved' || r.status === 'CheckedIn') && (
                    <Box sx={{ display: 'flex', gap: 0.8, mt: 0.8, flexWrap: 'wrap' }}>
                      {r.status === 'Reserved' && (
                        <Button
                          size='small'
                          variant='outlined'
                          color='success'
                          onClick={() => changeStatus(r.id, 'CheckedIn')}
                          sx={{ textTransform: 'none' }}>
                          Check In
                        </Button>
                      )}
                      <Button
                        size='small'
                        variant='outlined'
                        color='inherit'
                        onClick={() => changeStatus(r.id, 'Completed')}
                        sx={{ textTransform: 'none' }}>
                        Complete
                      </Button>
                      <Button
                        size='small'
                        variant='outlined'
                        color='error'
                        onClick={() => changeStatus(r.id, 'Cancelled')}
                        sx={{ textTransform: 'none' }}>
                        Cancel
                      </Button>
                    </Box>
                  )}
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Create dialog */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth='xs' fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>New Reservation</DialogTitle>
        <DialogContent sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel id='res-table'>Table</InputLabel>
            <Select
              labelId='res-table'
              label='Table'
              value={form.tableId}
              onChange={(e) => setForm({ ...form, tableId: e.target.value })}>
              {tables.map((t) => (
                <MenuItem key={t.id} value={t.id}>
                  {t.tableName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label='Customer Name'
            value={form.customerName}
            onChange={(e) => setForm({ ...form, customerName: e.target.value })}
            fullWidth
          />
          <TextField
            label='Mobile (10 digits)'
            value={form.mobile}
            onChange={(e) =>
              setForm({
                ...form,
                mobile: e.target.value.replace(/\D/g, '').slice(0, 10),
              })
            }
            fullWidth
          />
          <TextField
            label='Date & Time'
            type='datetime-local'
            value={form.reservationDateTime}
            onChange={(e) =>
              setForm({ ...form, reservationDateTime: e.target.value })
            }
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <TextField
              label='Guests'
              type='number'
              value={form.guestCount}
              onChange={(e) => setForm({ ...form, guestCount: e.target.value })}
              inputProps={{ min: 1 }}
              fullWidth
            />
            <TextField
              label='Hold (min)'
              type='number'
              value={form.durationMinutes}
              onChange={(e) =>
                setForm({ ...form, durationMinutes: e.target.value })
              }
              inputProps={{ min: 30, step: 30 }}
              fullWidth
            />
          </Box>
          <TextField
            label='Notes (optional)'
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            multiline
            minRows={2}
            fullWidth
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpen(false)} color='inherit'>
            Cancel
          </Button>
          <Button onClick={submit} variant='contained' disabled={saving}>
            {saving ? 'Saving…' : 'Reserve'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
